import React, { useState, useCallback, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
// Mightier Survey Engine
import {
  NumberQuestion,
  NumberResponse,
} from "@neuromotion-inc/mightier-survey-engine";
import { SurveyEngineIface } from "src/services/surveyHooks/useSurveyEngine";
// Styling
import { Slider, Container, Text, TextInput } from "@mantine/core";
import css from "./survey-number-input.module.css";

interface SurveyNumberInputProps {
  question: NumberQuestion;
  surveyEngine: SurveyEngineIface;
  questionIdToValidationErrorsMap: Map<string, string[]>;
  testId: string;
}

export const SurveyNumberInput = React.memo<SurveyNumberInputProps>(
  function SurveyNumberInput({
    question,
    surveyEngine,
    questionIdToValidationErrorsMap,
    testId,
  }) {
    const { t } = useTranslation("translation");

    const [selectedNumber, setSelectedNumber] = useState<number>(() => {
      const res = surveyEngine.getResponseForQuestion(
        question.id,
      ) as NumberResponse;
      return res?.responseValue;
    });

    const [touched, setTouched] = useState(true);

    const errors = useMemo(() => {
      return questionIdToValidationErrorsMap.get(question.id) ?? [];
    }, [question.id, questionIdToValidationErrorsMap]);

    const showError = touched && errors.length > 0;

    // Define hooks unconditionally
    const currAnswer = useMemo(() => {
      const response = surveyEngine.getResponseForQuestion(question.id);
      if (!response) {
        return "";
      }

      if (response.type !== "NUMBER") {
        Sentry.captureMessage("Response type mismatch in SurveyTextInput");
        return "";
      }

      return response.responseValue?.toString() || "";
    }, [surveyEngine, question.id]);

    const onInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setTouched(true);
        surveyEngine.recordResponse({
          question_id: question.id,
          responseValue: parseInt(e.currentTarget.value),
          type: "NUMBER",
        });
      },
      [surveyEngine, question.id],
    );

    // Dynamically generate marks for min, max, and middle
    const marks = useMemo(() => {
      const { min = 0, max = 0 } = question.validate || {};
      if (min > max) {
        return [];
      }

      // Calculate the middle value
      const middle = Math.floor((min + max) / 2);

      // Return marks for min, middle, and max
      return [
        { value: min, label: min.toString() },
        { value: middle, label: middle.toString() },
        { value: max, label: max.toString() },
      ];
    }, [question.validate]);

    const onSliderChange = useCallback((value: number) => {
      setTouched(true);
      setSelectedNumber(value);
    }, []);

    const onSliderChangeEnd = useCallback(
      (value: number) => {
        surveyEngine.recordResponse({
          question_id: question.id,
          responseValue: value,
          type: "NUMBER",
        });
      },
      [surveyEngine, question.id],
    );

    /**
     * INPUT UI TYPE
     */
    if (question.ui.inputType === "INPUT") {
      return (
        <Container>
          <TextInput
            size="lg"
            styles={{
              input: {
                fontSize: "21px", // Override Mantine's default font size
              },
            }}
            className={css["text-input"]}
            variant="unstyled"
            placeholder={t("survey.typeAnswer")}
            onBlur={onInputChange}
            value={currAnswer}
            onChange={onInputChange}
            error={showError ? errors.join(", ") : null}
            data-testid={testId}
          />
        </Container>
      );
    }

    /**
     * SLIDER UI TYPE
     */
    return (
      <Container>
        <Slider
          size="xl"
          color="blue"
          marks={marks}
          min={question.validate?.min || 0}
          max={question.validate?.max || 0}
          value={selectedNumber}
          onChange={onSliderChange}
          onChangeEnd={onSliderChangeEnd}
          data-testid={testId}
        />
        {/* Mantine Slider component doesn't have built in error state, so made own */}
        {showError ? (
          <Text mt="xl" color="red">
            {errors.join(", ")}
          </Text>
        ) : null}
      </Container>
    );
  },
);
